import { Handshake, UserCheck } from "lucide-react";
import {
  useDataProvider,
  useGetList,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
} from "ra-core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";

import type { CrmDataProvider } from "../providers/types";
import type { Contact } from "../types";

/**
 * Contacts created by hand have no lead submission, so they never reach the
 * Leads list. "Convert to lead" records one (and notifies the assignee);
 * "Create deal" then opens the deal form prefilled with this contact.
 */
export const ContactLeadActions = () => {
  const contact = useRecordContext<Contact>();
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<CrmDataProvider>();

  const { total, isPending } = useGetList(
    "lead_submissions",
    {
      pagination: { page: 1, perPage: 1 },
      filter: { contact_id: contact?.id },
    },
    { enabled: !!contact },
  );

  const { mutate: convert, isPending: isConverting } = useMutation({
    mutationFn: () => dataProvider.convertContactToLead(contact!.id),
    onSuccess: () => {
      notify(
        translate("resources.contacts.convert_to_lead.success", {
          _: "Contact converted to lead",
        }),
      );
      queryClient.invalidateQueries({ queryKey: ["lead_submissions"] });
      refresh();
    },
    onError: () =>
      notify(
        translate("resources.contacts.convert_to_lead.error", {
          _: "Failed to convert contact to lead",
        }),
        { type: "error" },
      ),
  });

  if (!contact || isPending) return null;

  return (
    <>
      {total === 0 && (
        <Button
          variant="outline"
          className="h-6 cursor-pointer"
          size="sm"
          disabled={isConverting}
          onClick={() => convert()}
        >
          <UserCheck className="w-4 h-4" />
          {translate("resources.contacts.convert_to_lead.action", {
            _: "Convert to lead",
          })}
        </Button>
      )}
      <Button asChild variant="outline" className="h-6" size="sm">
        <Link
          to="/deals/create"
          state={{
            record: {
              contact_ids: [contact.id],
              company_id: contact.company_id,
            },
          }}
        >
          <Handshake className="w-4 h-4" />
          {translate("resources.contacts.create_deal", {
            _: "Create deal",
          })}
        </Link>
      </Button>
    </>
  );
};
